import { useTranslation } from "react-i18next";
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  Type,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FontSelector from "@/components/FontSelector";
import ColorInput from "@/components/ColorInput";
import type { PaletteSubtitleColorRole, PaletteSubtitleLinks } from "@/lib/color/paletteEditorValue";
import { SubtitleAnimationCarousel } from "@/components/subtitles/SubtitleAnimationCarousel";
import {
  SubtitleLogoEditor,
  type SubtitleEditorLogoData,
} from "@/components/subtitles/SubtitleLogoEditor";
import { SubtitleTextTransformControl } from "@/components/subtitles/SubtitleTextTransformControl";
import {
  DEFAULT_SUBTITLE_TEXT_TRANSFORM,
  type SubtitleTextTransform,
} from "@/lib/subtitles/subtitleTextTransform";
import {
  DEFAULT_SUBTITLE_OUTLINE,
  SUBTITLE_SHADOW_OFFSET_MAX,
  SUBTITLE_SHADOW_SPREAD_MAX,
  SUBTITLE_STROKE_WIDTH_MAX,
  type SubtitleOutlineFields,
} from "@/lib/subtitles/subtitleOutline";
import { cn } from "@/lib/utils";

export interface SubtitleEditorData extends SubtitleOutlineFields {
  fontFamily: string;
  fontSize: number;
  textColor: string;
  highlightColor: string;
  highlightEnabled: boolean;
  highlightBoxEnabled: boolean;
  highlightBoxBackgroundColor: string;
  highlightBoxBorderRadius: number;
  highlightBoxPaddingX: number;
  highlightBoxPaddingY: number;
  backgroundColor: string;
  backgroundEnabled: boolean;
  backgroundOpacity: number;
  backgroundWidth: number;
  backgroundHeight: number;
  position: string;
  marginVertical: number;
  marginHorizontal: number;
  shadowEnabled: boolean;
  shadowColor: string;
  shadowSize: number;
  animation: string;
  textAlign: "left" | "center" | "right";
  textTransform: SubtitleTextTransform;
  removePunctuation: boolean;
  maxWordsPerLine: number;
}

export type { SubtitleEditorLogoData };

export const defaultSubtitleEditorData: SubtitleEditorData = {
  fontFamily: "Roboto",
  fontSize: 50,
  textColor: "#FFFFFF",
  highlightColor: "#CCFF00",
  highlightEnabled: true,
  highlightBoxEnabled: false,
  highlightBoxBackgroundColor: "#FFFFFF",
  highlightBoxBorderRadius: 20,
  highlightBoxPaddingX: 0.2,
  highlightBoxPaddingY: 0.1,
  backgroundColor: "#000000",
  backgroundEnabled: false,
  backgroundOpacity: 80,
  backgroundWidth: 100,
  backgroundHeight: 100,
  position: "bottom-center",
  marginVertical: 30,
  marginHorizontal: 10,
  shadowEnabled: true,
  shadowColor: "#000000",
  shadowSize: 50,
  ...DEFAULT_SUBTITLE_OUTLINE,
  animation: "Fade In",
  textAlign: "center",
  textTransform: DEFAULT_SUBTITLE_TEXT_TRANSFORM,
  removePunctuation: false,
  maxWordsPerLine: 6,
};

export const defaultSubtitleEditorLogoData: SubtitleEditorLogoData = {
  url: null,
  position: "bottom-right",
  size: 20,
  opacity: 70,
  horizontalMargin: 5,
  verticalMargin: 5,
};

interface ColorPickerFieldProps {
  pickerId: string;
  value: string;
  onChange: (color: string) => void;
}

const ColorPickerField = ({ pickerId, value, onChange }: ColorPickerFieldProps) => (
  <ColorInput value={value} onChange={onChange} testId={`color-${pickerId}`} />
);

interface PaletteLinkedColorFieldProps {
  label: string;
  pickerId: string;
  value: string;
  onChange: (color: string) => void;
  overridesPalette?: boolean;
  overrideHint: string;
  revertLabel: string;
  onRevert?: () => void;
}

const PaletteLinkedColorField = ({
  label,
  pickerId,
  value,
  onChange,
  overridesPalette = false,
  overrideHint,
  revertLabel,
  onRevert,
}: PaletteLinkedColorFieldProps) => (
  <div
    className={cn(
      "space-y-1",
      overridesPalette && "rounded-md border border-dashed border-muted-foreground/35 bg-muted/30 p-2",
    )}
  >
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {overridesPalette && (
      <p className="text-[10px] leading-tight text-muted-foreground">{overrideHint}</p>
    )}
    <div className="flex items-center gap-2">
      <ColorPickerField pickerId={pickerId} value={value} onChange={onChange} />
      {overridesPalette && onRevert && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-8 min-w-0 flex-1 px-2 text-[10px] font-medium leading-tight text-foreground"
          data-testid={`revert-palette-${pickerId}`}
          onClick={onRevert}
        >
          {revertLabel}
        </Button>
      )}
    </div>
  </div>
);

export interface SubtitleEditorProps {
  subtitleData: SubtitleEditorData;
  onSubtitleChange: (field: keyof SubtitleEditorData, value: any) => void;
  logoData: SubtitleEditorLogoData;
  onLogoChange: (field: keyof SubtitleEditorLogoData, value: any) => void;
  selectedSubtitleFontId: string;
  onFontSelect: (fontId: string, font: { family: string }) => void;
  onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo: boolean;
  className?: string;
  /** When false, logo is rendered by a sibling card (StyleSidePanel). */
  includeLogo?: boolean;
  /** When set, unlinked roles show that the picker overrides the palette default. */
  paletteSubtitleLinks?: PaletteSubtitleLinks | null;
  onRevertPaletteSubtitleColor?: (role: PaletteSubtitleColorRole) => void;
  /** Hide vertical position, margins, and the line-banner while a curve path owns placement. */
  hidePathControls?: boolean;
}

const SubtitleEditor = ({
  subtitleData,
  onSubtitleChange,
  logoData,
  onLogoChange,
  selectedSubtitleFontId,
  onFontSelect,
  onLogoUpload,
  isUploadingLogo,
  className,
  includeLogo = true,
  paletteSubtitleLinks = null,
  onRevertPaletteSubtitleColor,
  hidePathControls = false,
}: SubtitleEditorProps) => {
  const { t } = useTranslation();
  const overrideHint = t("configureSubtitles.editor.overridesPaletteDefault");
  const revertLabel = t("configureSubtitles.editor.revertToPaletteDefault");
  const overridesTextColor = paletteSubtitleLinks != null && !paletteSubtitleLinks.font;
  const overridesHighlightColor = paletteSubtitleLinks != null && !paletteSubtitleLinks.highlight;
  const overridesShadowColor = paletteSubtitleLinks != null && !paletteSubtitleLinks.shadow;

  return (
    <div className={cn("space-y-4 py-3", className)}>
      <SubtitleAnimationCarousel
        value={subtitleData.animation}
        onChange={(name) => onSubtitleChange("animation", name)}
      />

      <div className="space-y-2.5 px-4">
        <div className="flex items-center gap-2">
          <Type className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold">
            {t("configureSubtitles.editor.fontAndText")}
          </span>
        </div>
        <FontSelector
          value={selectedSubtitleFontId}
          onValueChange={(fontId, font) => onFontSelect(fontId, font)}
          label={t("configureSubtitles.editor.font")}
          showPreview={false}
          className="space-y-1"
        />
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {t("configureSubtitles.editor.fontSize")}: {subtitleData.fontSize}%
            </Label>
            <Slider
              value={[subtitleData.fontSize]}
              onValueChange={([v]) => onSubtitleChange("fontSize", v)}
              min={1}
              max={100}
              step={1}
            />
          </div>
          <PaletteLinkedColorField
            label={t("configureSubtitles.editor.textColor")}
            pickerId="textColor"
            value={subtitleData.textColor}
            onChange={(c) => onSubtitleChange("textColor", c)}
            overridesPalette={overridesTextColor}
            overrideHint={overrideHint}
            revertLabel={revertLabel}
            onRevert={
              onRevertPaletteSubtitleColor
                ? () => onRevertPaletteSubtitleColor("font")
                : undefined
            }
          />
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs text-muted-foreground">
              {t("configureSubtitles.editor.textAlign")}
            </Label>
            <div className="flex gap-0.5 rounded-md bg-muted/50 p-0.5">
              {(
                [
                  ["left", AlignLeft],
                  ["center", AlignCenter],
                  ["right", AlignRight],
                ] as const
              ).map(([align, Icon]) => (
                <Button
                  key={align}
                  variant={subtitleData.textAlign === align ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onSubtitleChange("textAlign", align)}
                  className="h-7 flex-1"
                >
                  <Icon className="h-3.5 w-3.5" />
                </Button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <SubtitleTextTransformControl
              value={subtitleData.textTransform}
              onChange={(value) => onSubtitleChange("textTransform", value)}
            />
          </div>
          <div className="flex items-center justify-between sm:col-span-2">
            <Label className="text-xs text-muted-foreground">
              {t("configureSubtitles.editor.removePunctuation")}
            </Label>
            <Switch
              checked={subtitleData.removePunctuation}
              onCheckedChange={(value) => onSubtitleChange("removePunctuation", value)}
            />
          </div>
        </div>
      </div>

      <Tabs defaultValue="highlight" className="px-4">
        <TabsList className="grid h-8 w-full grid-cols-3 p-0.5">
          <TabsTrigger value="highlight" className="h-7 px-1 text-[11px]">
            {t("configureSubtitles.editor.tabHighlight")}
          </TabsTrigger>
          <TabsTrigger value="position" className="h-7 px-1 text-[11px]">
            {t("configureSubtitles.editor.tabPosition")}
          </TabsTrigger>
          <TabsTrigger value="effects" className="h-7 px-1 text-[11px]">
            {t("configureSubtitles.editor.tabEffects")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="highlight" className="mt-2.5 space-y-2.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">
              {t("configureSubtitles.editor.highlightCurrentWord")}
            </Label>
            <Switch
              checked={subtitleData.highlightEnabled}
              onCheckedChange={(v) => onSubtitleChange("highlightEnabled", v)}
            />
          </div>
          {subtitleData.highlightEnabled && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <PaletteLinkedColorField
                label={t("configureSubtitles.editor.highlightColor")}
                pickerId="highlightColor"
                value={subtitleData.highlightColor}
                onChange={(c) => onSubtitleChange("highlightColor", c)}
                overridesPalette={overridesHighlightColor}
                overrideHint={overrideHint}
                revertLabel={revertLabel}
                onRevert={
                  onRevertPaletteSubtitleColor
                    ? () => onRevertPaletteSubtitleColor("highlight")
                    : undefined
                }
              />
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">
                  {t("configureSubtitles.editor.highlightBox")}
                </Label>
                <Switch
                  checked={subtitleData.highlightBoxEnabled}
                  onCheckedChange={(v) => onSubtitleChange("highlightBoxEnabled", v)}
                />
              </div>
              {subtitleData.highlightBoxEnabled && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("configureSubtitles.editor.highlightBoxColor")}
                    </Label>
                    <ColorPickerField
                      pickerId="highlightBoxColor"
                      value={subtitleData.highlightBoxBackgroundColor}
                      onChange={(c) => onSubtitleChange("highlightBoxBackgroundColor", c)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("configureSubtitles.editor.borderRadius")}:{" "}
                      {subtitleData.highlightBoxBorderRadius}px
                    </Label>
                    <Slider
                      value={[subtitleData.highlightBoxBorderRadius]}
                      onValueChange={([v]) => onSubtitleChange("highlightBoxBorderRadius", v)}
                      min={0}
                      max={50}
                      step={1}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("configureSubtitles.editor.paddingX")}: {subtitleData.highlightBoxPaddingX}
                    </Label>
                    <Slider
                      value={[subtitleData.highlightBoxPaddingX]}
                      onValueChange={([v]) => onSubtitleChange("highlightBoxPaddingX", v)}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("configureSubtitles.editor.paddingY")}: {subtitleData.highlightBoxPaddingY}
                    </Label>
                    <Slider
                      value={[subtitleData.highlightBoxPaddingY]}
                      onValueChange={([v]) => onSubtitleChange("highlightBoxPaddingY", v)}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="position" className="mt-2.5">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {!hidePathControls && (
              <>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.verticalPosition")}
              </Label>
              <div className="flex gap-0.5 rounded-md bg-muted/50 p-0.5">
                {(
                  [
                    ["top-center", AlignVerticalJustifyStart, 15],
                    ["middle-center", AlignVerticalJustifyCenter, 50],
                    ["bottom-center", AlignVerticalJustifyEnd, 25],
                  ] as const
                ).map(([pos, Icon, margin]) => (
                  <Button
                    key={pos}
                    variant={subtitleData.position === pos ? "default" : "ghost"}
                    size="sm"
                    onClick={() => {
                      onSubtitleChange("position", pos);
                      onSubtitleChange("marginVertical", margin);
                    }}
                    className="h-7 flex-1"
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.verticalMargin")}: {subtitleData.marginVertical}%
              </Label>
              <Slider
                value={[subtitleData.marginVertical]}
                onValueChange={([v]) => onSubtitleChange("marginVertical", v)}
                min={0}
                max={100}
                step={1}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.horizontalMargin")}: {subtitleData.marginHorizontal}%
              </Label>
              <Slider
                value={[subtitleData.marginHorizontal]}
                onValueChange={([v]) => onSubtitleChange("marginHorizontal", v)}
                min={0}
                max={30}
                step={1}
              />
            </div>
              </>
            )}
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.maxWordsPerLine")}: {subtitleData.maxWordsPerLine}
              </Label>
              <Slider
                value={[subtitleData.maxWordsPerLine]}
                onValueChange={([v]) => onSubtitleChange("maxWordsPerLine", v)}
                min={1}
                max={20}
                step={1}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="effects" className="mt-2.5 space-y-4">
          {!hidePathControls && (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.enableBackground")}
              </Label>
              <Switch
                checked={subtitleData.backgroundEnabled}
                onCheckedChange={(v) => onSubtitleChange("backgroundEnabled", v)}
              />
            </div>
            {subtitleData.backgroundEnabled && (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.backgroundColor")}
                  </Label>
                  <ColorPickerField
                    pickerId="backgroundColor"
                    value={subtitleData.backgroundColor}
                    onChange={(c) => onSubtitleChange("backgroundColor", c)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.opacity")}: {subtitleData.backgroundOpacity}%
                  </Label>
                  <Slider
                    value={[subtitleData.backgroundOpacity]}
                    onValueChange={([v]) => onSubtitleChange("backgroundOpacity", v)}
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.width")}: {subtitleData.backgroundWidth}%
                  </Label>
                  <Slider
                    value={[subtitleData.backgroundWidth]}
                    onValueChange={([v]) => onSubtitleChange("backgroundWidth", v)}
                    min={100}
                    max={200}
                    step={5}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.height")}: {subtitleData.backgroundHeight}%
                  </Label>
                  <Slider
                    value={[subtitleData.backgroundHeight]}
                    onValueChange={([v]) => onSubtitleChange("backgroundHeight", v)}
                    min={100}
                    max={200}
                    step={5}
                  />
                </div>
              </div>
            )}
          </div>
          )}

          <div className={hidePathControls ? "space-y-2.5" : "space-y-2.5 border-t border-border/40 pt-3"}>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.enableOutline")}
              </Label>
              <Switch
                data-testid="subtitle-outline-toggle"
                checked={subtitleData.strokeEnabled}
                onCheckedChange={(v) => onSubtitleChange("strokeEnabled", v)}
              />
            </div>
            {subtitleData.strokeEnabled && (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.outlineColor")}
                  </Label>
                  <ColorPickerField
                    pickerId="strokeColor"
                    value={subtitleData.strokeColor}
                    onChange={(c) => onSubtitleChange("strokeColor", c)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {t("configureSubtitles.editor.outlineWidth")}: {subtitleData.strokeWidth}%
                  </Label>
                  <Slider
                    data-testid="subtitle-outline-width"
                    value={[subtitleData.strokeWidth]}
                    onValueChange={([v]) => onSubtitleChange("strokeWidth", v)}
                    min={1}
                    max={SUBTITLE_STROKE_WIDTH_MAX}
                    step={1}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 border-t border-border/40 pt-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                {t("configureSubtitles.editor.enableShadow")}
              </Label>
              <Switch
                checked={subtitleData.shadowEnabled}
                onCheckedChange={(v) => onSubtitleChange("shadowEnabled", v)}
              />
            </div>
            {subtitleData.shadowEnabled && (
              <Tabs
                value={subtitleData.shadowStyle}
                onValueChange={(v) => onSubtitleChange("shadowStyle", v)}
              >
                <TabsList className="grid h-8 w-full grid-cols-2 p-0.5">
                  <TabsTrigger value="glow" className="h-7 py-0 text-xs" data-testid="subtitle-shadow-glow">
                    {t("configureSubtitles.editor.shadowStyleGlow")}
                  </TabsTrigger>
                  <TabsTrigger value="drop" className="h-7 py-0 text-xs" data-testid="subtitle-shadow-drop">
                    {t("configureSubtitles.editor.shadowStyleDrop")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            {subtitleData.shadowEnabled && (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <PaletteLinkedColorField
                  label={t("configureSubtitles.editor.shadowColor")}
                  pickerId="shadowColor"
                  value={subtitleData.shadowColor}
                  onChange={(c) => onSubtitleChange("shadowColor", c)}
                  overridesPalette={overridesShadowColor}
                  overrideHint={overrideHint}
                  revertLabel={revertLabel}
                  onRevert={
                    onRevertPaletteSubtitleColor
                      ? () => onRevertPaletteSubtitleColor("shadow")
                      : undefined
                  }
                />
                {subtitleData.shadowStyle === "drop" ? (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {t("configureSubtitles.editor.shadowSize")}: {subtitleData.shadowSpread}%
                      </Label>
                      <Slider
                        data-testid="subtitle-shadow-spread"
                        value={[subtitleData.shadowSpread]}
                        onValueChange={([v]) => onSubtitleChange("shadowSpread", v)}
                        min={0}
                        max={SUBTITLE_SHADOW_SPREAD_MAX}
                        step={1}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {t("configureSubtitles.editor.shadowOffsetX")}: {subtitleData.shadowOffsetX}%
                      </Label>
                      <Slider
                        data-testid="subtitle-shadow-offset-x"
                        value={[subtitleData.shadowOffsetX]}
                        onValueChange={([v]) => onSubtitleChange("shadowOffsetX", v)}
                        min={-SUBTITLE_SHADOW_OFFSET_MAX}
                        max={SUBTITLE_SHADOW_OFFSET_MAX}
                        step={1}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {t("configureSubtitles.editor.shadowOffsetY")}: {subtitleData.shadowOffsetY}%
                      </Label>
                      <Slider
                        data-testid="subtitle-shadow-offset-y"
                        value={[subtitleData.shadowOffsetY]}
                        onValueChange={([v]) => onSubtitleChange("shadowOffsetY", v)}
                        min={-SUBTITLE_SHADOW_OFFSET_MAX}
                        max={SUBTITLE_SHADOW_OFFSET_MAX}
                        step={1}
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("configureSubtitles.editor.shadowSize")}: {subtitleData.shadowSize}%
                    </Label>
                    <Slider
                      value={[subtitleData.shadowSize]}
                      onValueChange={([v]) => onSubtitleChange("shadowSize", v)}
                      min={0}
                      max={50}
                      step={1}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {includeLogo && (
        <div className="border-t border-border/40">
          <SubtitleLogoEditor
            logoData={logoData}
            onLogoChange={onLogoChange}
            onLogoUpload={onLogoUpload}
            isUploadingLogo={isUploadingLogo}
          />
        </div>
      )}
    </div>
  );
};

export default SubtitleEditor;
